import { alpha, Box, Typography, useTheme } from "@leap4/frontend";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
    DashboardAxisDto,
    DashboardBucketDto,
    DashboardSeriesDto,
} from "../../../api/generated/models";
import { formatMetric, signColor, signHex } from "./format";

/** The Overall chart: multi-axis lines over time, profit/loss background bands, and a selection you */

/** The viewBox width tracks the container's pixel width so one viewBox unit is exactly one CSS pixel. */
const H = 340;
const MIN_W = 320;
const PAD_T = 14;
const PAD_B = 28;
const PAD_L = 62;
const AXIS_W = 56;

export interface ISelection {
    /** Inclusive bucket indexes. i0 === i1 is a single-bucket click. */
    i0: number;
    i1: number;
    from: string;
    to: string;
}

interface IProps {
    buckets: DashboardBucketDto[];
    series: DashboardSeriesDto[];
    axes: DashboardAxisDto[];
    grain: string;
    selection: ISelection | null;
    onSelect: (selection: ISelection) => void;
    onClear: () => void;
    loading?: boolean;
}

/** Series whose sign decides the profit/loss bands, in preference order. */
const PROFIT_KEYS = ["edgePer1k", "profit"];

const shortLabel = (bucket: string, grain: string): string => {
    if (grain === "hour") return `${bucket.slice(8, 10)}/${bucket.slice(5, 7)} ${bucket.slice(11, 16)}`;
    return `${bucket.slice(8, 10)}/${bucket.slice(5, 7)}`;
};

const fullLabel = (bucket: string, grain: string): string => {
    const d = `${bucket.slice(8, 10)}/${bucket.slice(5, 7)}/${bucket.slice(0, 4)}`;
    if (grain === "hour") return `${d} ${bucket.slice(11, 16)}`;
    if (grain === "week") return `Week of ${d}`;
    return d;
};

/** At most `maxTicks` labels, always including the last bucket. */
const tickIndexes = (n: number, maxTicks: number): number[] => {
    if (n <= maxTicks) return Array.from({ length: n }, (_, i) => i);
    const step = Math.ceil((n - 1) / (maxTicks - 1));
    const out: number[] = [];
    for (let i = 0; i < n; i += step) out.push(i);
    if (out[out.length - 1] !== n - 1) out.push(n - 1);
    return out;
};

const DashboardChart = ({
    buckets,
    series,
    axes,
    grain,
    selection,
    onSelect,
    onClear,
    loading,
}: IProps): JSX.Element => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [W, setW] = useState<number>(1000);
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);
    const [drag, setDrag] = useState<{ a: number; b: number } | null>(null);
    /** Distinguishes a click from a drag, so a click can select rather than clear. */
    const movedRef = useRef(false);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const measure = () => setW(Math.max(MIN_W, Math.round(el.getBoundingClientRect().width)));
        measure();
        if (typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [buckets.length]);

    const n = buckets.length;
    const rightAxes = axes.slice(1);
    const padR = 14 + rightAxes.length * AXIS_W;

    const x = (i: number): number =>
        PAD_L + (n > 1 ? (i * (W - PAD_L - padR)) / (n - 1) : (W - PAD_L - padR) / 2);

    const axisById = useMemo(() => new Map(axes.map((a) => [a.id, a])), [axes]);

    /** 6% padding keeps a flat series off the frame edge. */
    const y = (v: number, axisId: number): number => {
        const ax = axisById.get(axisId);
        if (!ax) return H - PAD_B;
        const span = (ax.max - ax.min) || 1;
        const pad = span * 0.06;
        const lo = ax.min - pad;
        const hi = ax.max + pad;
        return H - PAD_B - ((v - lo) * (H - PAD_T - PAD_B)) / ((hi - lo) || 1);
    };

    const valueOf = (b: DashboardBucketDto, key: string): number | null => {
        const v = (b as unknown as Record<string, unknown>)[key];
        return typeof v === "number" && Number.isFinite(v) ? v : null;
    };

    const primary = useMemo(
        () => series.find((s) => PROFIT_KEYS.includes(s.key)) ?? null,
        [series]
    );

    const firstForecastIdx = useMemo(() => buckets.findIndex((b) => b.isForecast), [buckets]);

    /** Breaks the path on a null rather than interpolating across a gap. */
    const pathFor = (s: DashboardSeriesDto, forecast: boolean): string => {
        let d = "";
        let pen = false;
        buckets.forEach((b, i) => {
            const inHalf = forecast
                ? firstForecastIdx >= 0 && i >= firstForecastIdx - 1
                : firstForecastIdx < 0 || i < firstForecastIdx;
            if (!inHalf) {
                pen = false;
                return;
            }
            const v = valueOf(b, s.key);
            if (v === null) {
                pen = false;
                return;
            }
            d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v, s.axis).toFixed(1)} `;
            pen = true;
        });
        return d;
    };

    const midL = (i: number): number => (i === 0 ? x(0) : (x(i - 1) + x(i)) / 2);
    const midR = (i: number): number => (i === n - 1 ? x(n - 1) : (x(i) + x(i + 1)) / 2);

    const idxFromEvent = (e: React.MouseEvent<SVGSVGElement>): number => {
        const rect = e.currentTarget.getBoundingClientRect();
        const rel = ((e.clientX - rect.left) / rect.width) * W;
        return Math.max(0, Math.min(n - 1, Math.round(((rel - PAD_L) / (W - PAD_L - padR)) * (n - 1))));
    };

    const commit = (i0: number, i1: number): void => {
        onSelect({ i0, i1, from: buckets[i0].bucket, to: buckets[i1].bucket });
    };

    const onDown = (e: React.MouseEvent<SVGSVGElement>): void => {
        const i = idxFromEvent(e);
        movedRef.current = false;
        setDrag({ a: i, b: i });
    };

    const onMove = (e: React.MouseEvent<SVGSVGElement>): void => {
        const i = idxFromEvent(e);
        setHoverIdx(i);
        setDrag((prev) => {
            if (!prev) return prev;
            if (i !== prev.a) movedRef.current = true;
            return { ...prev, b: i };
        });
    };

    /** A drag always selects. */
    const onUp = (): void => {
        if (!drag) return;
        const i0 = Math.min(drag.a, drag.b);
        const i1 = Math.max(drag.a, drag.b);
        const wasClick = !movedRef.current && i0 === i1;
        setDrag(null);
        if (wasClick && selection) {
            onClear();
            return;
        }
        commit(i0, i1);
    };

    const onLeave = (): void => {
        setHoverIdx(null);
        setDrag(null);
    };

    const selRange = drag
        ? { i0: Math.min(drag.a, drag.b), i1: Math.max(drag.a, drag.b) }
        : selection
        ? { i0: selection.i0, i1: selection.i1 }
        : null;

    const hb = hoverIdx !== null && hoverIdx < n ? buckets[hoverIdx] : null;
    const ticks = tickIndexes(n, grain === "hour" ? 8 : 10);
    const leftAxis = axes[0];

    const gridColor = theme.palette.divider;
    const axisColor = theme.palette.text.disabled;
    const labelColor = theme.palette.text.secondary;

    if (n === 0) {
        return (
            <Box sx={{ py: 8, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    No data in this range.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{ position: "relative" }}
        >
            <Box
                component="svg"
                viewBox={`0 0 ${W} ${H}`}
                width={W}
                height={H}
                sx={{ display: "block", width: "100%", height: H, cursor: "crosshair", userSelect: "none" }}
                onMouseDown={onDown}
                onMouseMove={onMove}
                onMouseUp={onUp}
                onMouseLeave={onLeave}
            >
                {/* Profit / loss bands for the primary signal. Absent when no profit series is shown. */}
                {primary &&
                    buckets.map((b, i) => {
                        const v = valueOf(b, primary.key);
                        if (v === null || v === 0) return null;
                        return (
                            <rect
                                key={`band-${b.bucket}`}
                                x={midL(i)}
                                y={PAD_T}
                                width={Math.max(0, midR(i) - midL(i))}
                                height={H - PAD_T - PAD_B}
                                fill={v > 0 ? "rgba(5,150,105,0.07)" : "rgba(225,29,72,0.07)"}
                            />
                        );
                    })}

                {/* Edges sit on bucket midpoints, so the band covers each selected bucket fully. */}
                {selRange && (
                    <g>
                        <rect
                            x={midL(selRange.i0)}
                            y={PAD_T}
                            width={Math.max(0, midR(selRange.i1) - midL(selRange.i0))}
                            height={H - PAD_T - PAD_B}
                            fill={alpha(theme.palette.primary.main, 0.14)}
                        />
                        <line
                            x1={midL(selRange.i0)}
                            x2={midL(selRange.i0)}
                            y1={PAD_T}
                            y2={H - PAD_B}
                            stroke={theme.palette.primary.main}
                            strokeWidth={1.5}
                            opacity={0.8}
                        />
                        <line
                            x1={midR(selRange.i1)}
                            x2={midR(selRange.i1)}
                            y1={PAD_T}
                            y2={H - PAD_B}
                            stroke={theme.palette.primary.main}
                            strokeWidth={1.5}
                            opacity={0.8}
                        />
                    </g>
                )}

                {/* Left axis gridlines and ticks */}
                {leftAxis &&
                    leftAxis.ticks.map((v) => (
                        <g key={`lt-${v}`}>
                            <line
                                x1={PAD_L}
                                x2={W - padR}
                                y1={y(v, leftAxis.id)}
                                y2={y(v, leftAxis.id)}
                                stroke={gridColor}
                                strokeWidth={1}
                            />
                            <text
                                x={PAD_L - 6}
                                y={y(v, leftAxis.id) + 3}
                                textAnchor="end"
                                fontSize={10}
                                fill={signHex(v, labelColor)}
                            >
                                {formatMetric(v, leftAxis.unit, true)}
                            </text>
                        </g>
                    ))}

                {/* Right axes, colour-matched when they carry a single series */}
                {/* Sign beats series colour: a negative figure printed green is simply wrong. */}
                {rightAxes.map((ax, k) => {
                    const ox = W - padR + 8 + k * AXIS_W;
                    return (
                        <g key={`ax-${ax.id}`}>
                            {ax.ticks.map((v) => (
                                <text
                                    key={`rt-${ax.id}-${v}`}
                                    x={ox}
                                    y={y(v, ax.id) + 3}
                                    textAnchor="start"
                                    fontSize={10}
                                    fill={signHex(v, axisColor)}
                                >
                                    {formatMetric(v, ax.unit, true)}
                                </text>
                            ))}
                        </g>
                    );
                })}

                <line x1={PAD_L} x2={W - padR} y1={H - PAD_B} y2={H - PAD_B} stroke={gridColor} />
                <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={H - PAD_B} stroke={gridColor} />

                {/* Shaded band behind the forecast, so the boundary is visible without reading the legend */}
                {firstForecastIdx >= 0 && (
                    <>
                        <rect
                            x={midL(firstForecastIdx)}
                            y={PAD_T}
                            width={Math.max(0, W - padR - midL(firstForecastIdx))}
                            height={H - PAD_T - PAD_B}
                            fill="rgba(148,163,184,0.10)"
                        />
                        <line
                            x1={midL(firstForecastIdx)}
                            x2={midL(firstForecastIdx)}
                            y1={PAD_T}
                            y2={H - PAD_B}
                            stroke={axisColor}
                            strokeDasharray="3 3"
                        />
                    </>
                )}

                {series.map((s) => (
                    <React.Fragment key={s.key}>
                        <path
                            d={pathFor(s, false)}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={primary && s.key === primary.key ? 2.6 : 1.6}
                            opacity={primary && s.key === primary.key ? 1 : 0.85}
                        />
                        <path
                            d={pathFor(s, true)}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={primary && s.key === primary.key ? 2.6 : 1.6}
                            strokeDasharray="6 4"
                            opacity={0.75}
                        />
                    </React.Fragment>
                ))}

                {hoverIdx !== null && (
                    <line
                        x1={x(hoverIdx)}
                        x2={x(hoverIdx)}
                        y1={PAD_T}
                        y2={H - PAD_B}
                        stroke={theme.palette.text.primary}
                        strokeWidth={0.75}
                        strokeDasharray="2 2"
                    />
                )}
                {hoverIdx !== null &&
                    series.map((s) => {
                        const v = valueOf(buckets[hoverIdx], s.key);
                        if (v === null) return null;
                        return (
                            <circle
                                key={`dot-${s.key}`}
                                cx={x(hoverIdx)}
                                cy={y(v, s.axis)}
                                r={3}
                                fill={s.color}
                            />
                        );
                    })}

                {ticks.map((i) => (
                    <g key={`x-${i}`}>
                        <line
                            x1={x(i)}
                            x2={x(i)}
                            y1={H - PAD_B}
                            y2={H - PAD_B + 3}
                            stroke={axisColor}
                            strokeWidth={1}
                        />
                        <text
                            x={x(i)}
                            y={H - 9}
                            textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
                            fontSize={10}
                            fill={labelColor}
                        >
                            {shortLabel(buckets[i].bucket, grain)}
                        </text>
                    </g>
                ))}
            </Box>

            {hb && !drag && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 4,
                        left: `${Math.min(70, Math.max(1, (x(hoverIdx as number) / W) * 100))}%`,
                        bgcolor: "background.paper",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        boxShadow: 3,
                        px: 1.25,
                        py: 0.75,
                        pointerEvents: "none",
                        zIndex: 5,
                        minWidth: 190,
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>
                        {fullLabel(hb.bucket, grain)}
                        {hb.isForecast && (
                            <Box component="span" sx={{ ml: 0.75, color: "text.disabled", fontWeight: 400 }}>
                                forecast
                            </Box>
                        )}
                    </Typography>
                    {series.map((s) => (
                        <Box
                            key={`tt-${s.key}`}
                            sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 11 }}
                        >
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: s.color,
                                    flexShrink: 0,
                                }}
                            />
                            <Box component="span" sx={{ color: "text.secondary" }}>
                                {s.label}
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    ml: "auto",
                                    fontVariantNumeric: "tabular-nums",
                                    pl: 1.5,
                                    color: signColor(valueOf(hb, s.key)),
                                }}
                            >
                                {formatMetric(valueOf(hb, s.key), s.unit)}
                            </Box>
                        </Box>
                    ))}
                    {hb.topDriver && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                mt: 0.5,
                                pt: 0.5,
                                borderTop: 1,
                                borderColor: "divider",
                                fontSize: 11,
                            }}
                        >
                            <Box component="span" sx={{ color: "text.secondary" }}>
                                Top driver
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 110,
                                }}
                                title={hb.topDriver.name}
                            >
                                {hb.topDriver.name || "—"}
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    ml: "auto",
                                    fontVariantNumeric: "tabular-nums",
                                    color: signColor(hb.topDriver.value),
                                }}
                            >
                                {formatMetric(hb.topDriver.value, "currency", true)}
                            </Box>
                        </Box>
                    )}
                    <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, color: "text.disabled", fontSize: 10 }}
                    >
                        Click or drag to select
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    px: 0.5,
                    mt: 0.5,
                }}
            >
                {series.map((s) => (
                    <Box key={`lg-${s.key}`} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 2, bgcolor: s.color }} />
                        <Typography variant="caption" color="text.secondary">
                            {s.label}
                        </Typography>
                    </Box>
                ))}
                {primary && (
                    <Typography variant="caption" sx={{ ml: "auto", color: "text.disabled" }}>
                        Green / red bands: {primary.label} of each period
                    </Typography>
                )}
                {selection && (
                    <Typography
                        component="button"
                        variant="caption"
                        onClick={onClear}
                        sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 5,
                            px: 1,
                            py: 0.25,
                            bgcolor: "background.paper",
                            cursor: "pointer",
                        }}
                    >
                        Clear selection
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default DashboardChart;
